import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import {
  createUser,
  resetRegistration,
  saveRegistrationDraft,
  loadRegistrationDraft,
} from '../store/actions/registrationActions';
import {
  updateRegistrationField,
  updateRegistrationData,
  setCurrentStep,
  nextStep as nextStepAction,
  previousStep as previousStepAction,
  markStepCompleted,
  clearRegistrationError,
} from '../store/reducers/registrationSlice';
import type { NewUserRegistration } from '../types/registration';

export const useRegistration = () => {
  const dispatch = useAppDispatch();

  const newUser = useAppSelector((state) => state.registration.newUser);
  const currentStep = useAppSelector((state) => state.registration.currentStep);
  const totalSteps = useAppSelector((state) => state.registration.totalSteps);
  const completedSteps = useAppSelector((state) => state.registration.completedSteps);
  const isLoading = useAppSelector((state) => state.registration.isLoading);
  const error = useAppSelector((state) => state.registration.error);

  const isStepCompleted = useCallback(
    (step: number) => completedSteps.includes(step),
    [completedSteps]
  );

  const progress = useCallback(
    () => Math.round((completedSteps.length / totalSteps) * 100),
    [completedSteps.length, totalSteps]
  );

  const updateField = useCallback(
    (field: keyof NewUserRegistration, value: any) => {
      dispatch(updateRegistrationField({ field, value }));
    },
    [dispatch]
  );

  const updateData = useCallback(
    (data: Partial<NewUserRegistration>) => {
      dispatch(updateRegistrationData(data));
    },
    [dispatch]
  );

  const goToStep = useCallback(
    (step: number) => {
      if (step >= 1 && step <= totalSteps) {
        dispatch(setCurrentStep(step));
      }
    },
    [dispatch, totalSteps]
  );

  const nextStep = useCallback(() => {
    dispatch(nextStepAction());
  }, [dispatch]);

  const previousStep = useCallback(() => {
    dispatch(previousStepAction());
  }, [dispatch]);

  const completeStep = useCallback(() => {
    dispatch(markStepCompleted(currentStep));
    dispatch(nextStepAction());
  }, [dispatch, currentStep]);

  const submitRegistration = useCallback(async () => {
    const result = await dispatch(createUser(newUser));
    return {
      success: result.meta.requestStatus === 'fulfilled',
      user: result.meta.requestStatus === 'fulfilled' ? result.payload : null,
    };
  }, [dispatch, newUser]);

  const reset = useCallback(() => {
    dispatch(resetRegistration());
  }, [dispatch]);

  const saveDraft = useCallback(() => {
    dispatch(saveRegistrationDraft(newUser));
  }, [dispatch, newUser]);

  const loadDraft = useCallback(() => {
    dispatch(loadRegistrationDraft());
  }, [dispatch]);

  const clearError = useCallback(() => {
    dispatch(clearRegistrationError());
  }, [dispatch]);

  const isStep1Valid = useCallback(() => {
    return !!(
      newUser.email &&
      newUser.password &&
      newUser.confirmPassword &&
      newUser.password === newUser.confirmPassword
    );
  }, [newUser]);

  const isStep2Valid = useCallback(() => {
    return !!(
      newUser.firstName &&
      newUser.lastName &&
      newUser.dateOfBirth
    );
  }, [newUser]);

  const isStep3Valid = useCallback(() => {
    return !!(newUser.healthcareSpecialty);
  }, [newUser]);

  const isStep4Valid = useCallback(() => {
    return !!(newUser.city && newUser.state);
  }, [newUser]);

  const isCurrentStepValid = useCallback(() => {
    switch (currentStep) {
      case 1:
        return isStep1Valid();
      case 2:
        return isStep2Valid();
      case 3:
        return isStep3Valid();
      case 4:
        return isStep4Valid();
      default:
        return true; 
    }
  }, [currentStep, isStep1Valid, isStep2Valid, isStep3Valid, isStep4Valid]);

  const canSubmit = useCallback(() => {
    return isStep1Valid() && isStep2Valid() && isStep3Valid() && isStep4Valid();
  }, [isStep1Valid, isStep2Valid, isStep3Valid, isStep4Valid]);

  return {
    newUser,
    currentStep,
    totalSteps,
    completedSteps,
    isLoading,
    error,

    updateField,
    updateData,
    goToStep,
    nextStep,
    previousStep,
    completeStep,
    submitRegistration,
    reset,
    saveDraft,
    loadDraft,
    clearError,

    isStepCompleted,
    progress,
    isStep1Valid,
    isStep2Valid,
    isStep3Valid,
    isStep4Valid,
    isCurrentStepValid,
    canSubmit,
  };
};
